import { CrownIcon } from '@src/components/icons/CrownIcon';
import { useTranslation } from 'react-i18next';
import { Button, Stack, showDialog } from '@src/components';

export function HelpDeskRevokePremium({
  resource,
  id,
  onRevoked,
  onDenied,
}: {
  resource: 'users' | 'boxes';
  id: number;
  onRevoked: () => void;
  onDenied: () => void;
}) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const user = resource === 'users';
  const label = ru ? 'Отозвать Премиум' : 'Revoke Premium';
  function confirm(error = false) {
    let allBoxes = false;
    showDialog({
      title: `${label} · #${id}`,
      children: (
        <Stack gap={3}>
          {user ? (
            <fieldset>
              <legend>{ru ? 'Где отключить Премиум?' : 'Where should Premium be revoked?'}</legend>
              <label>
                <input
                  type="radio"
                  name="revoke-scope"
                  defaultChecked
                  onChange={() => {
                    allBoxes = false;
                  }}
                />{' '}
                {ru
                  ? 'Только у пользователя — доступ коробок сохранится'
                  : 'User only — keep box access'}
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  name="revoke-scope"
                  onChange={() => {
                    allBoxes = true;
                  }}
                />{' '}
                {ru ? 'У пользователя и всех его коробок' : 'User and all their boxes'}
              </label>
              <p>
                {ru
                  ? 'Все коробки, где пользователь — действующий организатор, включая архив и прошлые сезоны. Коробки, где он только участник, не изменятся.'
                  : 'All boxes where this user is an active organizer, including archived boxes and past seasons. Participant-only boxes stay unchanged.'}
              </p>
            </fieldset>
          ) : (
            <p>
              {ru
                ? 'Премиум отключится только у этой коробки. Доступ пользователей и других коробок сохранится.'
                : 'Revoke Premium only for this box. Users and other boxes keep their access.'}
            </p>
          )}
          <p>
            {ru
              ? 'Деньги не возвращаются. Данные игр и история платежей сохранятся.'
              : 'This does not issue a refund. Game data and payment history are preserved.'}
          </p>
          {error && (
            <p role="alert">
              {ru
                ? 'Не удалось отозвать Премиум. Попробуйте ещё раз.'
                : 'Could not revoke Premium. Please retry.'}
            </p>
          )}
        </Stack>
      ),
      cancel: ru ? 'Отмена' : 'Cancel',
      submit: label,
      closeLabel: ru ? 'Закрыть' : 'Close',
      testId: 'help-desk-revoke-premium-confirm',
      onSubmit: async () => {
        try {
          const response = await fetch(`/api/admin/help-desk/${resource}/${id}/premium/revoke`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ allBoxes }),
          });
          if ([401, 403].includes(response.status)) {
            onDenied();
            return;
          }
          if (!response.ok) throw new Error('Revocation failed');
          onRevoked();
        } catch (error) {
          confirm(true);
          throw error;
        }
      },
    });
  }
  return (
    <Button
      variant="ghost"
      className="help-desk__action-button"
      aria-label={label}
      title={label}
      icon={
        <span className="help-desk__revoked-crown">
          <CrownIcon />
          <svg viewBox="0 0 12 12" aria-hidden="true">
            <path d="m3 3 6 6m0-6-6 6" />
          </svg>
        </span>
      }
      onClick={() => confirm()}
    />
  );
}
